import authenticationFilter from './authentication-filter.js';
import {serverAuthenticationEntryPointFailureHandler} from './entry-point-failure-handler.js';
import {
    AuthenticationManager,
    ServerAuthenticationEntryPoint,
    ServerAuthenticationFailureHandler,
    ServerAuthenticationSuccessHandler
} from './types.js';
import {httpStatusEntryPoint} from './http-status-entry-point.js';
import {httpBasicEntryPoint} from './http-basic-entry-point.js';
import {delegatingEntryPoint} from './delegating-entry-point.js';
import {httpBasicAuthenticationConverter} from './http-basic-converter.js';
import {delegatingSuccessHandler} from './delegating-success-handler.js';
import {type SecurityContext} from './security-context.js';
import {MatchResult, ServerWebExchangeMatcher} from '../util/matchers.js';
import * as matchers from '../util/matchers.js';
import {HttpStatus} from '../../http/status.js';
import {AsyncLocalStorage} from 'node:async_hooks';
import type {ServerWebExchange} from '../../../types/web/server';

export default function httpBasic(opts: {
    readonly storage: AsyncLocalStorage<{securityContext?: Promise<SecurityContext>}>
    readonly manager: AuthenticationManager;
    readonly entryPoint?: ServerAuthenticationEntryPoint;
    readonly failureHandler?: ServerAuthenticationFailureHandler;
    readonly successHandlers?: ServerAuthenticationSuccessHandler[];
    defaultEntryPoints: Array<[ServerWebExchangeMatcher, ServerAuthenticationEntryPoint]>;
    defaultSuccessHandlers: ServerAuthenticationSuccessHandler[];
}) {

    const xhrMatcher: ServerWebExchangeMatcher = async (exchange: ServerWebExchange): Promise<MatchResult> => {
        const headers = exchange.request.headers;
        const h = headers.list('X-Requested-With');
        if (h.includes('XMLHttpRequest')) {
            return matchers.match();
        }
        return matchers.NO_MATCH;
    }

    const defaultEntryPoint = delegatingEntryPoint({
        entryPoints: [[xhrMatcher, httpStatusEntryPoint({httpStatus: HttpStatus.UNAUTHORIZED})]],
        defaultEntryPoint: httpBasicEntryPoint({})
    });

    const entryPoint = opts.entryPoint ?? defaultEntryPoint;

    const manager = opts.manager;

    const restMatcher = matchers.mediaType({
        mediaTypes: [
            'application/atom+xml',
            'application/x-www-form-urlencoded',
            'application/json',
            'application/octet-stream',
            'application/xml',
            'multipart/form-data',
            'text/xml'
        ],
        ignoredMediaTypes: ['*/*']
    });

    const notHtmlMatcher = matchers.not(matchers.mediaType({mediaTypes: ['text/html']}));
    const restNoHtmlMatcher = matchers.and([notHtmlMatcher, restMatcher]);
    const preferredMatcher = matchers.or([xhrMatcher, restNoHtmlMatcher]);
    opts.defaultEntryPoints.push([preferredMatcher, entryPoint]);
    const failureHandler = opts.failureHandler ?? serverAuthenticationEntryPointFailureHandler({entryPoint});
    const successHandler = delegatingSuccessHandler(opts.successHandlers ?? opts.defaultSuccessHandlers);
    const converter = httpBasicAuthenticationConverter({});
    return authenticationFilter({
        storage: opts.storage,
        manager,
        failureHandler,
        successHandler,
        converter
    });
}

